import React, { useState } from 'react';
import { TaggedContentTile } from 'components/molecules';
import { Button } from 'components/atoms';
import PropTypes, { any } from 'prop-types';
import { useOverlay } from 'queries/overlay';
import * as Styled from './CreativeHubGrid.styled';
import Overlay from '../overlay/Overlay';

/**
 * Renders the Article Grid.
 * @param {Array} items
 * @param {number} contentCountPerPage
 * @returns {ReactElement}
 */
const CreativeHubGrid = ({ items = [], contentCountPerPage = 9 }) => {
  const [numPages, setNumPages] = useState(1);
  const overlays = items.map(item => item.overlay?.sys?.id || null);
  const [currentOverlay, setCurrentOverlay] = useState(0);

  const {
    showOverlay,
    overlayData,
    handleOverlayOpen,
    handleOverlayClose,
  } = useOverlay();

  const handleSetOverlay = index => {
    setCurrentOverlay(index);
    handleOverlayOpen(items[index].overlay.sys.id);
  };

  const getNextOverlayIndex = index => {
    let currIndex = index + 1;
    while (currIndex < overlays.length) {
      if (overlays[currIndex]) {
        return currIndex;
      }
      currIndex += 1;
    }
    return null;
  };

  const getPreviousOverlayIndex = index => {
    let currIndex = index - 1;
    while (currIndex >= 0) {
      if (overlays[currIndex]) {
        return currIndex;
      }
      currIndex -= 1;
    }
    return null;
  };

  const renderGridPage = page => {
    const itemsToShow = items.slice(
      page * contentCountPerPage,
      (page + 1) * contentCountPerPage,
    );
    return (
      <Styled.GridPage key={page}>
        {itemsToShow.map(item => (
          <TaggedContentTile
            key={item.sys.id}
            tile={item}
            handleClick={() => {
              if (item.overlay?.sys?.id) {
                handleSetOverlay(items.indexOf(item));
              }
            }}
          />
        ))}
      </Styled.GridPage>
    );
  };

  return (
    <Styled.Container>
      <Overlay
        data={overlayData}
        handleClose={handleOverlayClose}
        isOpen={showOverlay && overlayData}
        previousTitle={
          getPreviousOverlayIndex(currentOverlay) !== null
            ? items[getPreviousOverlayIndex(currentOverlay)].title
            : undefined
        }
        handlePrevious={() => {
          const prevIndex = getPreviousOverlayIndex(currentOverlay);
          if (prevIndex !== null) {
            handleSetOverlay(prevIndex);
          }
        }}
        nextTitle={
          getNextOverlayIndex(currentOverlay) !== null
            ? items[getNextOverlayIndex(currentOverlay)].title
            : undefined
        }
        handleNext={() => {
          const nextIndex = getNextOverlayIndex(currentOverlay);
          if (nextIndex !== null) {
            handleSetOverlay(nextIndex);
          }
        }}
      />
      {[...Array(numPages).keys()].map(page => renderGridPage(page))}
      <Styled.CtaContainer>
        {numPages * contentCountPerPage < items.length && (
          <Button
            type="secondary"
            text="View More"
            onClick={() => setNumPages(numPages + 1)}
          />
        )}
        {numPages > 1 && numPages * contentCountPerPage < items.length && (
          <Button
            type="secondary"
            text="View Less"
            onClick={() => setNumPages(numPages - 1)}
          />
        )}
      </Styled.CtaContainer>
    </Styled.Container>
  );
};

CreativeHubGrid.propTypes = {
  /**
   * List of items
   */
  items: PropTypes.arrayOf(any),
  /**
   * number of tiles per page
   */
  contentCountPerPage: PropTypes.number,
};

export default CreativeHubGrid;
